import { JiraIssueFieldOptionAri } from '@atlassian/ari/jira/issue-field-option';
import { FieldValueLookupError } from '../../../common/errors';
import type {
	RadioButtonsPublicGetValue,
	RadioButtonsPublicSetValue,
} from '../../../common/fields/radio-buttons';
import type { PublicToInternalPropertyTransformerContext } from '../../../common/types';
import type { OptionsVisibility } from '../../../common/types/options-visibility';
import { applyOptionsVisibilityRule } from '../../../common/utils/apply-options-visibility-rule';
import { getAvailableOptionsVisibilityRule } from '../../../common/utils/get-available-options-visibility-rule';
import { getResourceIdfromAri } from '../../../common/utils/get-resource-id-from-ari';
import { transformOptionIdsToARI } from '../../../common/utils/options-visibility';
import { singleValueLookup } from '../../../common/utils/single-value-lookup';

// TODO: when FF `modern-issue-transition-fields-rollout-batch-2_bremb` is cleaned up, this function should be removed
export type RadioButtonsInternalAllowedValueOld = {
	id: string;
	value: string;
};

export type RadioButtonsInternalValue = {
	label: string;
	value: string;
} | null;

export type RadioButtonsInternalAllowedValue = {
	label: string;
	value: string;
};

// TODO: when FF `modern-issue-transition-fields-rollout-batch-2_bremb` is cleaned up, this function should be removed
export const radioButtonsSetValueTransformerOld = (
	value: RadioButtonsPublicSetValue,
	allowedValues?: RadioButtonsInternalAllowedValueOld[],
): RadioButtonsInternalValue => {
	if (value === null) {
		return null;
	}

	const foundValue = singleValueLookup<RadioButtonsInternalAllowedValueOld>(
		value,
		allowedValues,
		'id',
	);

	if (!foundValue) {
		throw new FieldValueLookupError();
	}

	return { value: foundValue.id, label: foundValue.value };
};

export const transformRadioButtonsSetValue = (
	value: RadioButtonsPublicSetValue,
	allowedValues: RadioButtonsInternalAllowedValue[],
	context: PublicToInternalPropertyTransformerContext,
): RadioButtonsInternalValue => {
	if (value === null) {
		return null;
	}

	const optionAri = JiraIssueFieldOptionAri.create({
		optionId: value,
		siteId: context.tenantContext.cloudId,
		activationId: context.tenantContext.activationId,
		fieldId: context.fieldContext.fieldId,
	}).toString();

	const foundValue = singleValueLookup<RadioButtonsInternalAllowedValue>(
		optionAri,
		allowedValues,
		'value',
	);

	if (!foundValue) {
		throw new FieldValueLookupError();
	}

	return foundValue;
};

// TODO: when FF `modern-issue-transition-fields-rollout-batch-2_bremb` is cleaned up, this function should be removed
export const radioButtonsGetValueTransformerOld = (
	value: RadioButtonsInternalValue,
): RadioButtonsPublicGetValue => {
	if (!value) {
		return null;
	}

	const { label, value: id } = value;

	return { id, value: label };
};

export const transformRadioButtonsGetValue = (
	internalValue: RadioButtonsInternalValue,
): RadioButtonsPublicGetValue => {
	if (!internalValue) {
		return null;
	}

	return {
		id: getResourceIdfromAri(internalValue.value),
		value: internalValue.label,
	};
};
export const transformRadioButtonsSetOptionsVisibility = (
	{ options, isVisible }: OptionsVisibility,
	lookupValues: RadioButtonsInternalAllowedValue[],
	context: PublicToInternalPropertyTransformerContext,
): RadioButtonsInternalAllowedValue[] => {
	const aris = transformOptionIdsToARI(options, context);
	return applyOptionsVisibilityRule(
		{
			options: aris,
			isVisible,
		},
		lookupValues,
		'value',
	);
};

// TODO: when FF `modern-issue-transition-fields-rollout-batch-2_bremb` is cleaned up, this function should be removed
export const transformRadioButtonsSetOptionsVisibilityOld = (
	optionsVisibility: OptionsVisibility,
	lookupValues: RadioButtonsInternalAllowedValueOld[],
): RadioButtonsInternalAllowedValueOld[] =>
	applyOptionsVisibilityRule(optionsVisibility, lookupValues, 'id');

// TODO: when FF `modern-issue-transition-fields-rollout-batch-2_bremb` is cleaned up, this function should be removed
export const transformRadioButtonsGetOptionsVisibility = (
	optionsVisibility: OptionsVisibility,
	lookupValues: RadioButtonsInternalAllowedValueOld[],
): OptionsVisibility => getAvailableOptionsVisibilityRule(optionsVisibility, lookupValues, 'id');
